import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { useAppSelector, useAppDispatch } from "../../../framework/customStore";
import { SectionHeader } from "../../layout/sectionHeader";
import { MonitorViewVehicles } from "./monitorViewVehicles";
import { MonitorViewWorkShifts } from "./monitorViewWorkShifts";
import { MonitorViewVehicleRoutes } from "./monitorViewVehicleRoutes";
import { MonitorViewSidebarProps } from "../../../models/monitorView/monitorViewTypes";
import { TabsEnum } from "../monitorViewUtils";
import { MonitorViewEmployees } from "./monitorViewEmployees";
import SidebarHeader from "./sidebarHeader";
import {
    setSelectedEmployeeId,
    setSidebarTitle,
    toggleWorkShiftView,
    setSelectedWorkShift,
} from "../../../store/monitorViewSlice";
import { getCurrentWorkShift } from "../../../services/workShiftService";
import { handleVehicleSelect } from "../monitorViewUtils";
import { useTranslation } from "react-i18next";

export const MonitorViewSidebar = ({
    workShiftSidebarProps,
    vehicleSidebarProps,
    employeeSidebarProps,
    activeEmployeesAndVehicles
}: MonitorViewSidebarProps) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    const {
        loading,
        activeTab: tab,
        sidebarTitle,
        isWorkShiftView,
        isVehicleRouteView,
        vehicleRoute,
        selectedVehicleId,
        tripStartDate,
        vehicles
    } = useAppSelector((state) => state.monitorView);

    const { activeVehicles, activeEmployees } = activeEmployeesAndVehicles;

    const onEmployeeSelect = async (employeeId: string) => {
        if (employeeId) {
            dispatch(setSelectedEmployeeId(employeeId));
            const workShift = await getCurrentWorkShift(employeeId, true);
            if (workShift) {
                dispatch(setSelectedWorkShift(workShift));
                dispatch(setSidebarTitle(workShift?.workShiftTimeSlots?.[0]?.employeeName || ""));
                dispatch(toggleWorkShiftView());
            }
        }
    };

    const onVehicleSelect = (vehicleId: string) => {
        handleVehicleSelect(
            vehicleId,
            vehicles,
            tripStartDate,
            isVehicleRouteView,
            dispatch,
            t
        );
    };

    const vehicleRouteSidebarProps = useMemo(() => ({
        data: vehicleRoute ? vehicleRoute : [],
        selectedId: selectedVehicleId,
        setSelectedId: () => { },
        header: sidebarTitle
    }), [vehicleRoute, sidebarTitle, selectedVehicleId]);

    return (
        <Box width={375} height="100%" display="flex" flexDirection="column">
            <SectionHeader position="sticky">
                <SidebarHeader
                    workShiftSidebarProps={workShiftSidebarProps}
                    vehicleRouteSidebarProps={vehicleRouteSidebarProps}
                    header={sidebarTitle}
                    activeVehicles={activeVehicles}
                    activeEmployees={activeEmployees}
                />
            </SectionHeader>
            {loading ? (
                <Box display="flex" justifyContent="center" p={2}>
                    <CircularProgress />
                </Box>
            ) : (
                <Box sx={{ overflowY: "auto", paddingTop: 1 }}>
                    {isWorkShiftView ? (
                        <MonitorViewWorkShifts {...workShiftSidebarProps} />
                    ) : isVehicleRouteView ? (
                        <MonitorViewVehicleRoutes {...vehicleRouteSidebarProps} />
                    ) : (
                        tab === TabsEnum.VEHICLES ? (
                            <MonitorViewVehicles {...vehicleSidebarProps} onSelect={onVehicleSelect} />
                        ) : (
                            <MonitorViewEmployees {...employeeSidebarProps} onSelect={onEmployeeSelect} />
                        )
                    )}
                </Box>
            )}
        </Box>
    );
};